// Analytics helper. When analytics consent is granted, every event is
// DELIVERED to GA4 via `gtag('event', name, {...safe, ...context, send_to: G-…})`
// — explicit destination routing to the GA4 tag only.
//
// It is NO LONGER pushed into `window.dataLayer` as `{event, ...}`. The only
// GTM container we run (`GTM-WGFZFB8R`) is Meta-only, and a container turns
// every entry of the data layer it listens on into a GTM customEvent — both a
// plain object push and a gtag command. `send_to` does not isolate a Google
// command from a GTM trigger. The Meta container now runs on its own
// `window.marketingDataLayer` (`src/lib/marketing-datalayer.ts`) and receives
// only curated events, so Google measurement can never fire a Meta tag.
//
// Consent Mode v2 gates Google's own reads too; skipping the call entirely is
// the belt-and-suspenders layer on top of it.
//
// Never pass PII: no names, emails, phones, addresses, form field values,
// payment data, or freeform text. Use field NAMES/TYPES for validation errors.
//
// A central `sanitizeParams` scrubber runs on every event as defense-in-depth,
// and the shared safe page context (`src/lib/page-context.ts`) is attached to
// EVERY event — callers can never supply or override it.

import { getConsent } from "./consent";
import { GA4_MEASUREMENT_ID, ensureGtag, isDestinationConfigured } from "./google-tag";
import { buildPageContext, CONTEXT_RESERVED_KEYS, safePagePath } from "./page-context";
import { isPublicTrackableRoute } from "./public-routes";

export { safePagePath };

type EventParams = Record<string, unknown>;

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}

// Keys that are NEVER allowed to leave the browser as analytics params.
// Matched by lowercased-substring on the key name.
const BLOCKED_KEY_PATTERNS: RegExp[] = [
  // Contact / identity
  /(^|_)(name|first_?name|last_?name|full_?name)$/,
  /(^|_)email/,
  /(^|_)phone/,
  /(^|_)address/,
  /(^|_)(zip|postal|postcode)/,
  // Free text
  /(^|_)(message|notes?|description|comment|freeform|freetext|body)/,
  /(^|_)file(_?name)?$/,
  /(^|_)photo/,
  // Session / CRM identifiers
  /(^|_)sid$/,
  /(session|contact|lead|crm|quote|hubspot|hs)_?id$/,
  /(^|_)token$/,
  // Financials / measurements
  /(^|_)(price|total|cost|amount|savings|revenue|value)$/,
  /(^|_)(low|high|mid|min|max)$/,
  /(^|_)(usage|kwh|kw|system_size|sqft|square_?feet|measurement)/,
  /(^|_)lead_?score/,
  // Equipment / utility inferences
  /(^|_)(utility|provider)$/,
  /(^|_)(panel_brand|unsafe_panel|panel_upgrade_flagged|roof_type|materials?|hardware|selected_hardware)/,
  /(^|_)(tier)$/,
  // Payment
  /(^|_)(card|cc|cvv|payment|checkout)/,
];

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE_RE = /(?:\+?\d[\s\-().]*){7,}/;
const MAX_STR = 120;

const isBlockedKey = (k: string): boolean => {
  const key = k.toLowerCase();
  return BLOCKED_KEY_PATTERNS.some((re) => re.test(key));
};

const warnDrop = (reason: string, key: string) => {
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    // Never log the value — reason and key only.
    // eslint-disable-next-line no-console
    console.warn(`[analytics] dropped param ${JSON.stringify(key)}: ${reason}`);
  }
};

/**
 * Strip query/hash from any URL-looking string; return same-origin pathname
 * or external hostname. Returns `undefined` if unsafe.
 */
const sanitizeDestinationString = (v: string): string | undefined => {
  if (!v) return undefined;
  if (/^(tel:|sms:|mailto:|javascript:|data:)/i.test(v)) return undefined;
  // Plain pathname — keep, strip query/hash defensively.
  if (v.startsWith("/")) return v.split(/[?#]/)[0].slice(0, MAX_STR);
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const url = new URL(v, origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    if (typeof window !== "undefined" && url.origin === window.location.origin) {
      return url.pathname.slice(0, MAX_STR);
    }
    return url.hostname.slice(0, MAX_STR);
  } catch {
    return undefined;
  }
};

const DESTINATION_KEYS = new Set(["destination", "dest", "href", "url", "link"]);

// Explicit allowlist for keys that would otherwise be caught by the generic
// blocked-name patterns but are known-safe schema labels (never values).
const ALLOWED_KEYS = new Set(["field_name", "field_type"]);

// Strict hostname: lowercase letters, digits, dot, hyphen; no path/query/hash/@/colon.
const HOSTNAME_RE = /^[a-z0-9]([a-z0-9-]{0,62}(?<!-)\.)+[a-z]{2,63}$/;

const sanitizeHostname = (v: string): string | undefined => {
  const s = v.trim().toLowerCase();
  if (!s || s.length > MAX_STR) return undefined;
  if (!HOSTNAME_RE.test(s)) return undefined;
  return s;
};

/**
 * Central defense-in-depth scrubber. Called on every analytics event before
 * anything reaches dataLayer.
 * - drops keys matching PII / sensitive patterns (with a small explicit allowlist)
 * - rejects string values that look like email/phone
 * - strips query/hash from URL-like fields; allows only same-origin pathname
 *   or external hostname
 * - referrer_host: strict hostname-only, never routed through URL sanitizer
 * - caps string length
 * - only primitive string/number/boolean values (rejects arrays/objects)
 */
export function sanitizeParams(params: EventParams): EventParams {
  const out: EventParams = {};
  for (const [rawKey, rawVal] of Object.entries(params)) {
    // Guard the reserved `event` slot — sanitized version stored on event name.
    if (rawKey === "event") {
      warnDrop("reserved key", rawKey);
      continue;
    }
    if (rawVal === undefined || rawVal === null) continue;
    if (!ALLOWED_KEYS.has(rawKey) && isBlockedKey(rawKey)) {
      warnDrop("blocked-key-pattern", rawKey);
      continue;
    }
    const t = typeof rawVal;
    if (t === "number") {
      if (!Number.isFinite(rawVal as number)) continue;
      out[rawKey] = rawVal;
      continue;
    }
    if (t === "boolean") {
      out[rawKey] = rawVal;
      continue;
    }
    if (t !== "string") {
      warnDrop("non-primitive", rawKey);
      continue;
    }
    let s = (rawVal as string).trim();
    if (!s) continue;
    if (EMAIL_RE.test(s)) { warnDrop("looks-like-email", rawKey); continue; }
    if (PHONE_RE.test(s)) { warnDrop("looks-like-phone", rawKey); continue; }
    // referrer_host: strict hostname only, never through URL sanitizer.
    if (rawKey === "referrer_host") {
      const host = sanitizeHostname(s);
      if (!host) { warnDrop("invalid-hostname", rawKey); continue; }
      out[rawKey] = host;
      continue;
    }
    if (DESTINATION_KEYS.has(rawKey.toLowerCase()) || /^https?:\/\//i.test(s) || s.includes("?") || s.includes("#")) {
      const cleaned = sanitizeDestinationString(s);
      if (!cleaned) { warnDrop("unsafe-destination", rawKey); continue; }
      s = cleaned;
    }
    if (s.length > MAX_STR) s = s.slice(0, MAX_STR);
    out[rawKey] = s;
  }
  return out;
}

/**
 * Keys a caller may NEVER set: they steer where the event goes, who it is
 * attributed to, or what page context Google records. We own all of them.
 * `page_path` and the rest of the shared context are included, so a caller can
 * never override the sanitized routing/context fields.
 */
const RESERVED_KEYS = new Set([
  "send_to",
  "event",
  "event_callback",
  "event_timeout",
  "groups",
  ...CONTEXT_RESERVED_KEYS,
  "user_id",
  "user_data",
  "user_properties",
  "client_id",
  "session_id",
  "gclid",
  "gbraid",
  "wbraid",
]);

/**
 * Fire an analytics event. No-op unless analytics consent is granted AND the
 * current route is a public, trackable one.
 *
 * Delivers to GA4 only, through gtag with explicit `send_to` routing. There is
 * no `window.dataLayer` event push any more (see the module header).
 *
 * The shared SAFE page context is attached to EVERY event, not just page_view.
 * A caller that wants to measure a different route passes `page_path`; it is
 * used only to build the context and is never trusted verbatim.
 */
export function trackEvent(event: string, params: EventParams = {}): boolean {
  if (typeof window === "undefined") return false;
  if (!getConsent().analytics) return false;
  if (!isPublicTrackableRoute(window.location.pathname)) return false;
  try {
    const sanitized = sanitizeParams(params);
    const safe: EventParams = {};
    for (const [k, v] of Object.entries(sanitized)) {
      if (RESERVED_KEYS.has(k)) { warnDrop("reserved key", k); continue; }
      safe[k] = v;
    }
    // Page context is ours, computed here — never taken from the caller.
    const context = buildPageContext(
      typeof params.page_path === "string" ? params.page_path : undefined,
    ) as unknown as EventParams;

    const gtag = ensureGtag();
    // Report success ONLY when GA4 is actually configured and the command
    // reached the real gtag queue. A caller that de-duplicates a page view must
    // never commit its key on a dropped call.
    if (!gtag || !isDestinationConfigured(GA4_MEASUREMENT_ID)) return false;
    gtag("event", event, { ...safe, ...context, send_to: GA4_MEASUREMENT_ID });
    return true;
  } catch {
    // Swallow — analytics must never break the UI — but report the failure so
    // the caller does not record the event as delivered.
    return false;
  }
}

/**
 * Fire a consent-lifecycle marker on the Google data layer. No vendor consumes
 * it today (the Meta container listens on `marketingDataLayer`), so it carries
 * no PII, sets no storage, and is never sent to GA4 without consent.
 */
export function trackConsentLifecycle(event: string, params: EventParams = {}): void {
  if (typeof window === "undefined") return;
  try {
    const safe = sanitizeParams(params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...safe });
  } catch { /* noop */ }
}

/**
 * Historically this buffered pre-choice events and replayed them once consent
 * was granted. That replayed activity the visitor had not yet consented to, so
 * the buffer is GONE: an event fired while consent is unknown or denied is
 * dropped for good. After a late grant, only the current page and new activity
 * are measured.
 *
 * The signature is kept so callers do not have to change.
 */
export function trackEventDeferred(event: string, params: EventParams = {}): boolean {
  if (!getConsent().analytics) return false;
  return trackEvent(event, params);
}
